import axios, { AxiosError } from "axios";
import Cookies from "universal-cookie";

function getCsrfToken() {
  const meta = document.querySelector("meta[name=csrf-token]");
  const token = meta && meta.getAttribute("content");

  return token ?? false;
}
const csrfToken = getCsrfToken();

interface ApiResponse {
  ok: boolean;
  status: number;
  body: any;
}
export class ApiResponseError extends Error {
  body: any;
  status: number;
  constructor(response: ApiResponse, ...args: any[]) {
    // super("Api response error", ...args); // This not working with the TS upgrade but i don't if its needed
    super("Api response error");
    this.name = "ApiResponseError";
    this.body = response.body;
    this.status = response.status;
  }
}
const parseResponse = async (response: Response) => ({
  ok: response.ok,
  status: response.status,
  body: await response.json(),
});

const SIGNED_OUT_PATH = "/users/sign_in?expired=1";

const isAxiosError = (error: unknown): error is AxiosError =>
  typeof error === "object" && error !== null && "isAxiosError" in error;

const redirectIfSignedOut = (status: number) => {
  if (status === 401) {
    window.location.href = SIGNED_OUT_PATH;
  }
};

interface GetRequest {
  url: string;
  headers?: HeadersInit;
}
const getVenueHeaders = (): Record<string, string> => {
  const cookies = new Cookies();
  // The venueId is set in the app/Navigation/PageNavigation
  // Its less than ideal but it works for now.
  // In the future we could use a context provider to pass it down with hooks
  // Or maybe include it in the cookies

  const venueId = (window as any)?.currentVenueId;
  if (!!venueId && typeof venueId == "string") {
    return {
      "X-Current-Venue-Id": venueId,
    };
  } else {
    return {};
  }
};
export const getTextRequest = async ({ url, headers }: GetRequest) => {
  const myHeaders = new Headers({
    ...getVenueHeaders(),
    ...headers,
  });
  const myInit: RequestInit = {
    method: "GET",
    headers: myHeaders,
    mode: "cors",
    credentials: "include",
    cache: "default",
  };

  const response = await fetch(url, myInit);
  const responseText = await response.text();
  if (!response.ok) {
    redirectIfSignedOut(response.status);
    throw new ApiResponseError({
      ok: response.ok,
      status: response.status,
      body: responseText,
    });
  }
  return responseText;
};
export const getJsonRequest = async ({ url, headers }: GetRequest) => {
  const cookies = new Cookies();

  const myHeaders = new Headers({
    "Content-Type": "application/json",
    ...getVenueHeaders(),
    ...headers,
  });
  const myInit: RequestInit = {
    method: "GET",
    headers: myHeaders,
    mode: "cors",
    credentials: "include",
    cache: "default",
  };

  const response = await fetch(url, myInit);
  const parsedResponse = await parseResponse(response);
  if (!response.ok) {
    redirectIfSignedOut(response.status);
    throw new ApiResponseError(parsedResponse);
  }
  return parsedResponse;
};

interface BodyRequest {
  url: string;
  body: any;
  headers?: HeadersInit;
}
interface DeleteRequest {
  url: string;
  body?: any;
  headers?: HeadersInit;
}
export const deleteJsonRequest = async ({
  url,
  body,
  headers,
}: DeleteRequest) => {
  const cookies = new Cookies();
  const myHeaders = new Headers({
    ...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {}),
    "Content-Type": "application/json",
    ...getVenueHeaders(),
    ...headers,
  });
  const myInit: RequestInit = {
    method: "DELETE",
    headers: myHeaders,
    mode: "cors",
    credentials: "include",
    cache: "default",
    body: JSON.stringify(body),
  };
  const response = await fetch(url, myInit);
  if (!response.ok) {
    redirectIfSignedOut(response.status);
    throw Error();
  }
  return await parseResponse(response);
};

// Use this when the response is not empty after deleting an object
export const deleteRequest = async ({ url, body, headers }: DeleteRequest) => {
  const myHeaders = new Headers({
    ...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {}),
    "Content-Type": "application/json",
    ...getVenueHeaders(),
    ...headers,
  });
  const myInit: RequestInit = {
    method: "DELETE",
    headers: myHeaders,
    mode: "cors",
    credentials: "include",
    cache: "default",
    body: JSON.stringify(body),
  };
  const response = await fetch(url, myInit);
  if (!response.ok) {
    redirectIfSignedOut(response.status);
    throw Error();
  }
  return {
    ok: response.ok,
    status: response.status,
    body: response.body,
  };
};

export const patchJsonRequest = async ({ url, body, headers }: BodyRequest) => {
  const myHeaders = new Headers({
    ...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {}),

    "Content-Type": "application/json",
    ...getVenueHeaders(),
    ...headers,
  });
  const myInit: RequestInit = {
    method: "PATCH",
    headers: myHeaders,
    mode: "cors",
    credentials: "include",
    cache: "default",
    body: JSON.stringify(body),
  };
  const response = await fetch(url, myInit);
  const parsedResponse = await parseResponse(response);
  if (!response.ok) {
    redirectIfSignedOut(response.status);
    throw new ApiResponseError(parsedResponse);
  }
  return parsedResponse;
};

export const postJsonRequest = async ({ url, body, headers }: BodyRequest) => {
  const csrfToken = getCsrfToken();
  const myHeaders = new Headers({
    ...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {}),
    "Content-Type": "application/json",
    ...getVenueHeaders(),
    ...headers,
  });
  const myInit: RequestInit = {
    method: "POST",
    headers: myHeaders,
    mode: "cors",
    credentials: "include",
    cache: "default",
    body: JSON.stringify(body),
  };
  const response = await fetch(url, myInit);
  const parsedResponse = await parseResponse(response);
  if (!response.ok) {
    redirectIfSignedOut(response.status);
    throw new ApiResponseError(parsedResponse);
  }
  return parsedResponse;
};

export const uploadJsonRequest = async ({
  url,
  body,
}: {
  url: string;
  body: NotTypedYet;
}) => {
  const formData = new FormData();
  Object.keys(body).forEach((key) => formData.append(key, body[key] as string));
  const myHeaders = {
    ...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {}),
    "Content-Type": "multipart/form-data",
    ...getVenueHeaders(),
  };
  const config = {
    headers: myHeaders,
    mode: "cors",
    credentials: "include",
    cache: "default",
  };
  try {
    const response = await axios.put(url, formData, config);
    return { ...response, body: response.data };
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      redirectIfSignedOut(error.response.status);
    }
    throw error;
  }
};
